import React, { useEffect, useState } from 'react';
import { api } from '../api.js';
import { errMessage } from '../i18n.js';
import { useT } from '../lang.js';
import Icon from '../Icon.jsx';
import Modal from '../Modal.jsx';
import PageSkeleton from '../Skeleton.jsx';

const ROLES = ['OWNER', 'MANAGER', 'CASHIER', 'WAITER', 'KITCHEN'];

export default function Staff() {
  const t = useT();
  const [list, setList] = useState(null);
  const [form, setForm] = useState({ role: 'WAITER' });
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  // Düzenleme popup'ta: ad, rol, PIN ve şifre aynı yerde dursun — satır
  // içinde dört ayrı alan telefonda okunmuyordu.
  const [editing, setEditing] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const load = () => api('/api/restaurant/staff').then(setList).catch((e) => setErr(errMessage(t, e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function create() {
    setErr(''); setMsg(''); setBusy(true);
    try {
      await api('/api/restaurant/staff', { method: 'POST', body: form });
      setForm({ role: 'WAITER' });
      setMsg(t('staff.added'));
      load();
    } catch (e) {
      setErr(errMessage(t, e));
    } finally {
      setBusy(false);
    }
  }

  async function patch(id, body) {
    setErr(''); setMsg('');
    try {
      await api(`/api/restaurant/staff/${id}`, { method: 'PATCH', body });
      load();
      return true;
    } catch (e) {
      setErr(errMessage(t, e));
      return false;
    }
  }

  if (err && !list) return <p className="err">{err}</p>;
  if (!list) return <PageSkeleton shape="table" />;

  return (
    <>
      <div className="card">
        <h2>{t('nav.staff')}</h2>
        <table>
          <thead>
            <tr><th>{t('name')}</th><th>{t('email')}</th><th>{t('staff.role')}</th><th>PIN</th><th>{t('status')}</th><th></th></tr>
          </thead>
          <tbody>
            {list.map((s) => (
              <tr key={s.id}>
                <td>{s.name}</td>
                <td className="muted">{s.email}</td>
                <td>
                  <select value={s.role} onChange={(e) => patch(s.id, { role: e.target.value })}>
                    {ROLES.map((k) => <option key={k} value={k}>{t(`role.${k}`)}</option>)}
                  </select>
                </td>
                <td>
                  {/* PIN tek dokunuşla açılıp kapanır: tablet girişini kapatmak
                      servis sırasında en sık yapılan iş. */}
                  <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
                    {s.hasPin ? <span className="badge ok">{t('staff.hasPin')}</span> : <span className="badge">{t('none')}</span>}
                    {s.hasPin
                      ? (
                        <button className="btn-sm" title={t('staff.pinRemove')} aria-label={t('staff.pinRemove')}
                          onClick={() => confirm(t('staff.confirmPinRemove', { name: s.name }))
                            && patch(s.id, { pin: '' })}>
                          <Icon n="ban" />
                        </button>
                      ) : (
                        <button className="btn-sm" title={t('staff.pinAdd')} aria-label={t('staff.pinAdd')}
                          onClick={() => setEditing(s)}>
                          <Icon n="keypad" />
                        </button>
                      )}
                  </div>
                </td>
                <td>{s.active ? <span className="badge ok">{t('staff.active')}</span> : <span className="badge bad">{t('staff.disabled')}</span>}</td>
                <td>
                  <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
                    <button onClick={() => setEditing(s)}><Icon n="pencil" />{t('edit')}</button>
                    <button onClick={() => patch(s.id, { active: !s.active })}>
                      <Icon n="power" />{s.active ? t('staff.disable') : t('staff.enable')}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h2>{t('staff.new')}</h2>
        <div className="grid2">
          <div><label>{t('name')}</label><input value={form.name || ''} onChange={set('name')} /></div>
          <div><label>{t('email')}</label><input value={form.email || ''} onChange={set('email')} /></div>
          <div>
            <label>{t('staff.role')}</label>
            <select value={form.role} onChange={set('role')}>
              {ROLES.map((k) => <option key={k} value={k}>{t(`role.${k}`)}</option>)}
            </select>
          </div>
          <div><label>{t('staff.passwordFor')}</label><input type="password" value={form.password || ''} onChange={set('password')} /></div>
          <div><label>{t('staff.pinFor')}</label><input value={form.pin || ''} onChange={set('pin')} inputMode="numeric" maxLength={6} /></div>
        </div>
        <button className="primary" style={{ marginTop: 12 }} disabled={busy} onClick={create}><Icon n="userPlus" />{t('add')}</button>
        <p className="muted" style={{ marginBottom: 0 }}>{t('staff.roleNote')}</p>
      </div>

      {editing && (
        <StaffSheet staff={editing} roles={ROLES} t={t} onClose={() => setEditing(null)}
          onSave={async (body) => {
            const ok = await patch(editing.id, body);
            if (ok) { setMsg(t('staff.saved')); setEditing(null); }
          }} />
      )}

      {msg && <p className="ok">{msg}</p>}
      {err && <p className="err">{err}</p>}
    </>
  );
}

/**
 * Personel düzenleme. Boş bırakılan alan DEĞİŞTİRİLMEZ: şifre ve PIN kutuları
 * mevcut değeri göstermez (hash saklanıyor, geri okunamaz) — boş gönderilirse
 * eskisi kalsın, yanlışlıkla silinmesin. PIN'i kaldırmak ayrı düğme.
 */
function StaffSheet({ staff, roles, t, onClose, onSave }) {
  const [form, setForm] = useState({ name: staff.name || '', role: staff.role, pin: '', password: '' });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = () => {
    const body = {};
    if (form.name.trim() && form.name !== staff.name) body.name = form.name.trim();
    if (form.role !== staff.role) body.role = form.role;
    if (form.pin) body.pin = form.pin;
    if (form.password) body.password = form.password;
    onSave(body);
  };

  return (
    <Modal title={staff.name || staff.email} subtitle={staff.email} onClose={onClose}
      footer={
        <div className="row">
          <button onClick={onClose}><Icon n="x" />{t('cancel')}</button>
          <span className="spacer" />
          <button className="primary" onClick={submit}><Icon n="save" />{t('save')}</button>
        </div>
      }>
      <div className="grid2">
        <div><label>{t('name')}</label><input value={form.name} onChange={set('name')} /></div>
        <div>
          <label>{t('staff.role')}</label>
          <select value={form.role} onChange={set('role')}>
            {roles.map((k) => <option key={k} value={k}>{t(`role.${k}`)}</option>)}
          </select>
        </div>
        <div>
          <label>{t('staff.pinNew')}</label>
          <input value={form.pin} onChange={set('pin')} inputMode="numeric" maxLength={6} placeholder="••••" />
        </div>
        <div>
          <label>{t('staff.newPassword')}</label>
          <input type="password" value={form.password} onChange={set('password')} placeholder="••••••••" />
        </div>
      </div>

      <div className="row" style={{ marginTop: 12 }}>
        {staff.hasPin && (
          <button onClick={() => onSave({ pin: '' })}><Icon n="ban" />{t('staff.pinRemove')}</button>
        )}
      </div>
      <p className="muted" style={{ marginBottom: 0 }}>{t('staff.editNote')}</p>
    </Modal>
  );
}
